import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type { ChatDetail, ChatList, TicketType } from "../api/types";
import type { PersonChoice } from "../tickets/api";

export const CHATS = ["chats"] as const;
const chatKey = (id: string) => [...CHATS, "detail", id] as const;
const chatPath = (id: string) => `/api/chats/${encodeURIComponent(id)}`;

/** My chats (admins: every open chat too). Live updates refresh this; the poll is a safety net. */
export function useChats(enabled = true) {
  return useQuery({
    queryKey: [...CHATS, "list"],
    queryFn: ({ signal }) => api<ChatList>("/api/chats", { signal }),
    refetchInterval: 120_000,
    enabled,
  });
}

export function useChat(id: string) {
  return useQuery({
    queryKey: chatKey(id),
    queryFn: ({ signal }) => api<ChatDetail>(chatPath(id), { signal }),
    refetchInterval: 60_000,
  });
}

/** Every change answers with the chat as it now is: show that, and refresh the list. */
function useChatChange<T>(id: string, request: (input: T) => Promise<ChatDetail>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (chat) => {
      queryClient.setQueryData(chatKey(id), chat);
      void queryClient.invalidateQueries({ queryKey: [...CHATS, "list"] });
    },
  });
}

export function useStartChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message, clientRequestId }: { message: string; clientRequestId: string }) =>
      api<ChatDetail>("/api/chats", {
        method: "POST",
        body: { message, client_request_id: clientRequestId },
      }),
    onSuccess: (chat) => {
      queryClient.setQueryData(chatKey(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: [...CHATS, "list"] });
    },
  });
}

/** Support starts a chat with someone (DECISIONS #50); their open chat, if they have one. */
export function useStartChatWith() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ person, message, clientRequestId }: { person: PersonChoice; message: string; clientRequestId: string }) =>
      api<ChatDetail>("/api/chats/with", {
        method: "POST",
        body: { person, message, client_request_id: clientRequestId },
      }),
    onSuccess: (chat) => {
      queryClient.setQueryData(chatKey(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: [...CHATS, "list"] });
    },
  });
}

export function useSendChatMessage(id: string) {
  return useChatChange(id, ({ body, clientRequestId }: { body: string; clientRequestId: string }) =>
    api<ChatDetail>(`${chatPath(id)}/messages`, {
      method: "POST",
      body: { body, client_request_id: clientRequestId },
    }),
  );
}

export function useEndChat(id: string) {
  return useChatChange(id, () => api<ChatDetail>(`${chatPath(id)}/end`, { method: "POST" }));
}

export function useTakeChat(id: string) {
  return useChatChange(id, () => api<ChatDetail>(`${chatPath(id)}/take`, { method: "POST" }));
}

export interface ChatTicketInput {
  title: string;
  type: TicketType;
  service_id: string | null;
}

export function useChatTicket(id: string) {
  const queryClient = useQueryClient();
  return useChatChange(id, async (input: ChatTicketInput) => {
    const chat = await api<ChatDetail>(`${chatPath(id)}/ticket`, { method: "POST", body: input });
    void queryClient.invalidateQueries({ queryKey: ["tickets", "list"] });
    return chat;
  });
}

/** I've seen this chat up to now (clears its unread count). */
export function useMarkChatRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`${chatPath(id)}/read`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...CHATS, "list"] }),
  });
}
